import { useCallback, useEffect, useRef, useState, type PointerEventHandler } from 'react';

import { resolveTooltipAt } from '../../../model/screencast-tooltips/screencastTooltipResolver';
import type { ResolvedTooltip, ScreencastTooltips } from '../../../model/screencast-tooltips/types';

import type { GetImagePointFromClientPosition, TooltipScreenPosition } from './types';

type UseScreencastHoverTooltipParams = {
  isEnabled: boolean;
  screencastTooltips?: ScreencastTooltips | null;
  getImagePointFromClientPosition: GetImagePointFromClientPosition;
};

export function useScreencastHoverTooltip({
  isEnabled,
  screencastTooltips,
  getImagePointFromClientPosition,
}: UseScreencastHoverTooltipParams) {
  const [resolvedTooltip, setResolvedTooltip] = useState<ResolvedTooltip | null>(null);
  const [tooltipScreenPosition, setTooltipScreenPosition] = useState<TooltipScreenPosition | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const pendingPointerRef = useRef<{
    wrapper: HTMLDivElement;
    clientX: number;
    clientY: number;
  } | null>(null);

  const clearTooltip = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    pendingPointerRef.current = null;
    setResolvedTooltip(null);
    setTooltipScreenPosition(null);
  }, []);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      pendingPointerRef.current = null;
    };
  }, [getImagePointFromClientPosition, isEnabled, screencastTooltips]);

  const handlePointerMove: PointerEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (!isEnabled || !screencastTooltips) {
        clearTooltip();
        return;
      }

      pendingPointerRef.current = {
        wrapper: event.currentTarget,
        clientX: event.clientX,
        clientY: event.clientY,
      };
      if (animationFrameRef.current !== null) {
        return;
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        animationFrameRef.current = null;
        const pointer = pendingPointerRef.current;
        pendingPointerRef.current = null;
        if (!pointer) {
          return;
        }

        const imagePoint = getImagePointFromClientPosition(pointer.wrapper, pointer.clientX, pointer.clientY);
        if (!imagePoint) {
          clearTooltip();
          return;
        }

        const nextResolvedTooltip = resolveTooltipAt(screencastTooltips, imagePoint.x, imagePoint.y);
        if (!nextResolvedTooltip) {
          clearTooltip();
          return;
        }

        setResolvedTooltip(nextResolvedTooltip);
        setTooltipScreenPosition({ x: pointer.clientX, y: pointer.clientY });
      });
    },
    [clearTooltip, getImagePointFromClientPosition, isEnabled, screencastTooltips],
  );

  const handleMouseLeave = useCallback(() => {
    clearTooltip();
  }, [clearTooltip]);

  return {
    resolvedTooltip,
    tooltipScreenPosition,
    clearTooltip,
    handlePointerMove,
    handleMouseLeave,
  };
}
